import { formatPercentage } from '@helpers';
import clsx from 'clsx';
import * as d3 from 'd3';
import { useId } from 'react';

import { getHumanFriendlyTicks } from '../helpers/chart-ticks/chart-ticks';
import { checkHasProfit } from '../helpers/chart-values/chart-values';
import { TextLabel } from '../high-low-chart/components/text-label/TextLabel';
import styles from './HorizontalHighLow.module.scss';

export interface HorizontalHighLowProps {
  data: {
    low: number;
    median: number;
    high: number;
  };
  width: number;
  height: number;
  withLabels?: boolean;
}

const margin = {
  top: 44,
  right: 20,
  bottom: 20,
  left: 20,
};

const BAR_HEIGHT_PX = 12;
const BAR_HALF_WIDTH_PX = BAR_HEIGHT_PX / 2;
const BAR_THIRD_WIDTH_PX = BAR_HEIGHT_PX / 3;
const CIRCLE_Y_POSITION = margin.top + BAR_HALF_WIDTH_PX;

export const HorizontalHighLow = ({
  data: { low, median, high },
  width,
  height,
  withLabels,
}: HorizontalHighLowProps) => {
  const { chartTicks, maxRounded, minRounded } = getHumanFriendlyTicks({
    min: low,
    max: high,
  });

  const Scale = d3
    .scaleLinear()
    .domain([maxRounded, minRounded])
    .range([width - margin.right, margin.left]);

  const xHigh = Scale(high);
  const xLow = Scale(low);
  const yMedian = Scale(median);

  const x0 = Scale(0);

  const clipPathId = useId();
  const hasMedianProfit = checkHasProfit(median);

  const lowStart = xLow;
  const lowEnd = high < 0 ? xHigh : x0;
  const lowBarWidth = lowEnd - lowStart;

  const highEnd = xHigh;

  const highBarWidth = highEnd - lowEnd;
  const fullWidth = highEnd - lowStart;

  return (
    <svg
      className={styles.rootSvg}
      data-testid="horizontal-high-low"
      viewBox={`0 0 ${width} ${height}`}
    >
      <g data-testid="high-low-bar">
        <clipPath id={clipPathId}>
          <rect
            width={fullWidth}
            x={xLow}
            y={margin.top}
            height={BAR_HEIGHT_PX}
            rx={BAR_HALF_WIDTH_PX}
            ry={BAR_HALF_WIDTH_PX}
          />
        </clipPath>

        <g clipPath={`url(#${clipPathId})`} data-testid="bars">
          {low < 0 && (
            <rect
              className={clsx(styles.rect, {
                [styles.rectProfit]: checkHasProfit(low),
              })}
              data-testid="low-bar"
              height={BAR_HEIGHT_PX}
              x={xLow}
              y={margin.top}
              width={lowBarWidth}
            />
          )}
          {high > 0 && (
            <rect
              className={clsx(styles.rect, {
                [styles.rectProfit]: checkHasProfit(high),
              })}
              data-testid="high-bar"
              height={BAR_HEIGHT_PX}
              x={x0}
              y={margin.top}
              width={highBarWidth}
            />
          )}
        </g>

        <g
          data-testid="median-label"
          transform={`translate(${yMedian}, ${CIRCLE_Y_POSITION})`}
        >
          <g className={styles.animatedGroup}>
            <circle className={styles.circle} r={BAR_THIRD_WIDTH_PX} />
            {withLabels && (
              <TextLabel
                hasProfit={hasMedianProfit}
                label={formatPercentage(median, { suffix: '%' })}
                x={-24}
                y={-(margin.top - BAR_HEIGHT_PX)}
              />
            )}
          </g>
        </g>
      </g>

      {withLabels && (
        <g data-testid="horizontal-high-low-ticks">
          {chartTicks.map(tickValue => (
            <g
              key={tickValue}
              transform={`translate(${Scale(tickValue)}, 80)`}
              data-testid="horizontal-high-low-tick"
            >
              <line
                className={styles.axisLine}
                y1={-8}
                y2={-16}
                strokeWidth={tickValue === 0 ? 2 : 1}
              />
              <text
                className={styles.axisText}
                textAnchor="middle"
                x={0}
                y={8}
                alignmentBaseline="middle"
              >
                {tickValue}%
              </text>
            </g>
          ))}
        </g>
      )}
    </svg>
  );
};
